import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const CONSENT_KEY = "cookie_consent";

function getInitialConsent() {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored === "accepted" || stored === "rejected") return stored;
    if (typeof document !== "undefined") {
      if (document.cookie.includes(`${CONSENT_KEY}=accepted`)) return "accepted";
      if (document.cookie.includes(`${CONSENT_KEY}=rejected`)) return "rejected";
    }
  } catch {}
  return null;
}

function setConsent(value) {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {}
  if (typeof document !== "undefined") {
    const maxAgeDays = 180;
    document.cookie = `${CONSENT_KEY}=${value}; path=/; max-age=${maxAgeDays * 24 * 60 * 60}; SameSite=Lax`;
  }
  // Matomo starts in cookieless/anonymous mode (`requireCookieConsent`, set in
  // public/index.html) so it can still count visits/pageviews for everyone
  // without needing consent. Accepting upgrades it to full cookie-based
  // tracking (returning-visitor recognition); declining explicitly tells it
  // to stay cookieless rather than leaving it in whatever state it was in.
  if (typeof window !== "undefined" && window._paq) {
    window._paq.push([value === "accepted" ? "setCookieConsentGiven" : "forgetCookieConsentGiven"]);
  }
}

function CookieConsent() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(() => getInitialConsent());
  const [visible, setVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const s = getInitialConsent();
    setStatus(s);
    if (!s) {
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  // Both choices are final — declining essential-only cookies still lets
  // the member use the site; it just means analytics.js won't track them
  // (see its own CONSENT_KEY check). Blocking the whole app behind a forced
  // "accept or leave" wall isn't real consent.
  if (status === "accepted" || status === "rejected") return null;

  const handleAccept = () => {
    setVisible(false);
    setTimeout(() => { setConsent("accepted"); setStatus("accepted"); }, 400);
  };

  const handleReject = () => {
    setVisible(false);
    setTimeout(() => { setConsent("rejected"); setStatus("rejected"); }, 400);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

        .cc-wrap {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          transform: translateY(${visible ? '0px' : '100%'});
          opacity: ${visible ? '1' : '0'};
          transition:
            transform 0.5s cubic-bezier(0.22, 1, 0.36, 1),
            opacity 0.4s ease;
          z-index: 9999;
          width: 100%;
          font-family: 'Plus Jakarta Sans', sans-serif;
          /* Anchored flush to the bottom edge so it reads as a bar, not a
             floating card mid-page, and never masks content above it. */
          padding: 0 16px env(safe-area-inset-bottom, 0px);
          box-sizing: border-box;
          display: flex;
          justify-content: center;
          /* Only the visible card should intercept clicks — otherwise this
             full-width fixed strip blocks interaction with whatever sits
             behind its transparent left/right margins too. */
          pointer-events: none;
        }

        .cc-card {
          background: #1c3d2e;
          border-radius: 14px 14px 0 0;
          overflow: hidden;
          box-shadow:
            0 -2px 4px rgba(0,0,0,0.12),
            0 -8px 24px rgba(0,0,0,0.22),
            inset 0 1px 0 rgba(255,255,255,0.07);
          display: flex;
          align-items: stretch;
          width: 100%;
          max-width: 780px;
          pointer-events: auto;
        }

        .cc-stripe {
          width: 4px;
          min-width: 4px;
          background: #faa002;
          border-radius: 0;
          flex-shrink: 0;
        }

        .cc-inner {
          flex: 1;
          padding: 14px 18px;
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .cc-icon-wrap {
          width: 36px;
          height: 36px;
          min-width: 36px;
          border-radius: 9px;
          background: rgba(250, 160, 2, 0.15);
          border: 1px solid rgba(250, 160, 2, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .cc-content {
          flex: 1;
          min-width: 0;
        }

        .cc-title {
          font-size: 13.5px;
          font-weight: 700;
          color: #f5f7f0;
          margin-bottom: 3px;
          letter-spacing: -0.01em;
        }

        .cc-desc {
          font-size: 12px;
          color: rgba(245, 247, 240, 0.62);
          line-height: 1.5;
          letter-spacing: -0.005em;
        }

        .cc-actions {
          display: flex;
          gap: 8px;
          flex-shrink: 0;
          align-items: center;
        }

        .cc-btn-reject {
          padding: 8px 14px;
          border-radius: 999px;
          border: 1px solid rgba(245, 247, 240, 0.2);
          background: transparent;
          color: rgba(245, 247, 240, 0.75);
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
          letter-spacing: -0.01em;
        }

        .cc-btn-reject:hover {
          background: rgba(245, 247, 240, 0.08);
          border-color: rgba(245, 247, 240, 0.35);
          color: #f5f7f0;
        }

        .cc-btn-accept {
          padding: 8px 16px;
          border-radius: 999px;
          border: none;
          background: #faa002;
          color: #1c3d2e;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
          letter-spacing: -0.01em;
          box-shadow: 0 2px 12px rgba(250, 160, 2, 0.4);
        }

        .cc-btn-accept:hover {
          background: #fbb024;
          box-shadow: 0 4px 18px rgba(250, 160, 2, 0.55);
          transform: translateY(-1px);
        }

        .cc-btn-accept:active {
          transform: translateY(0);
          box-shadow: 0 2px 8px rgba(250, 160, 2, 0.35);
        }

        @media (max-width: 560px) {
          .cc-wrap {
            padding: 0;
          }
          .cc-card {
            border-radius: 0;
            max-width: none;
            /* Desktop lays the stripe and content out side by side (row, the
               default). At this breakpoint .cc-stripe becomes a full-width top
               border instead of a left accent bar, so the card itself must switch
               to a column layout too — otherwise the stripe (now 100% wide) fills
               the entire row and pushes .cc-inner (with the actual title/description/
               buttons) completely off the right edge of the screen, leaving only
               the stripe visible and the banner effectively non-functional. */
            flex-direction: column;
          }
          .cc-stripe {
            width: 100%;
            min-width: unset;
            height: 3px;
          }
          .cc-inner {
            flex-direction: column;
            align-items: stretch;
            gap: 6px;
            padding: 10px 14px calc(10px + env(safe-area-inset-bottom, 0px));
          }
          .cc-icon-wrap { display: none; }
          .cc-content { text-align: left; }
          .cc-desc {
            display: -webkit-box;
            -webkit-line-clamp: 1;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          .cc-actions {
            width: 100%;
            flex-direction: row-reverse;
          }
          .cc-btn-accept, .cc-btn-reject {
            flex: 1;
            text-align: center;
            justify-content: center;
          }
        }
      `}</style>

      <div className="cc-wrap" role="dialog" aria-label="Cookie consent">
        <div className="cc-card" ref={cardRef}>
          <div className="cc-stripe" />
          <div className="cc-inner">
            <div className="cc-icon-wrap">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 3C7.03 3 3 7.03 3 12s4.03 9 9 9 9-4.03 9-9" stroke="#faa002" strokeWidth="1.8" strokeLinecap="round"/>
                <path d="M15 3.5C15 4.88 16.12 6 17.5 6S20 4.88 20 3.5 18.88 1 17.5 1 15 2.12 15 3.5z" fill="#faa002" opacity="0.7"/>
                <path d="M20 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z" fill="rgba(250,160,2,0.4)"/>
                <circle cx="8.5"  cy="10.5" r="1.2" fill="#f5f7f0" opacity="0.9"/>
                <circle cx="12"   cy="8.5"  r="0.9" fill="#faa002"/>
                <circle cx="14.5" cy="13"   r="1.1" fill="#f5f7f0" opacity="0.9"/>
                <circle cx="9.5"  cy="14.5" r="0.85" fill="#faa002" opacity="0.8"/>
                <circle cx="12.5" cy="16"   r="0.9" fill="#f5f7f0" opacity="0.7"/>
              </svg>
            </div>
            <div className="cc-content">
              <div className="cc-title">🍪 {t("pages.cookie.bannerTitle")}</div>
              <div className="cc-desc">
                {t("pages.cookie.bannerDesc")}
              </div>
            </div>
            <div className="cc-actions">
              <button type="button" className="cc-btn-reject" onClick={handleReject}>
                {t("pages.cookie.decline")}
              </button>
              <button type="button" className="cc-btn-accept" onClick={handleAccept}>
                {t("pages.cookie.acceptContinueFull")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default CookieConsent;